/**
 * Sinal de idade da loja — Lei Felca (Lei 15.211/2025, arts. 12-13).
 *
 * O `birthDate` que o jogador digita no cadastro NÃO vale como verificação de
 * idade (art. 9º); ele é aferição de baixa confiança. O sinal confiável vem da
 * app store, e a Google Play passou a devolvê-lo no Brasil em 17/03/2026
 * (Digital ECA). Como a Play Age Signals API não tem plugin Capacitor
 * publicado, o plugin foi escrito dentro do projeto Android
 * (`android/app/src/main/java/com/gamelabs/rocketanks/AgeSignalsPlugin.java`).
 *
 * Aqui fica a metade em TypeScript: mapear a faixa crua da loja para as faixas
 * que o servidor entende e reportar. Duas regras que valem a pena repetir:
 *
 * 1. O servidor só aceita sinal do cliente na direção PROTETIVA. Um "adulto"
 *    vindo daqui é recusado (`adult_signal_requires_verification`) — seria
 *    autodeclaração com outro nome. Por isso este módulo nem tenta enviar.
 * 2. Sem sinal, o servidor trata a conta como potencialmente de menor. Falha de
 *    rede, Play Store velha ou aparelho fora da região elegível nunca
 *    enfraquecem a proteção — só deixam de reforçá-la.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";

/** Faixas aceitas pelo servidor (`server/ageProtection.ts`). */
export type AgeBracket = "under13" | "13_15" | "16_17" | "adult";

export interface AgeSignalsResult {
  available: boolean;
  shared: boolean;
  ageLower: number | null;
  ageUpper: number | null;
}

interface AgeSignalsPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  checkAgeSignals(): Promise<AgeSignalsResult>;
}

const AgeSignals = registerPlugin<AgeSignalsPlugin>("AgeSignals");

/**
 * A faixa vem como intervalo fechado (`[13, 15]`) ou aberto no topo
 * (`[18, null]`), e a loja pode devolver faixas customizadas — por isso o
 * mapeamento olha os limites em vez de comparar com uma lista fixa.
 *
 * Regra de arredondamento: sempre para a faixa MAIS protetiva que o intervalo
 * comporta. Um `[12, 15]` (faixa customizada que cruza a fronteira dos 13
 * anos) vira `under13`, não `13_15`.
 */
export function bracketFromAgeRange(
  ageLower: number | null,
  ageUpper: number | null,
): AgeBracket | null {
  if (ageLower === null && ageUpper === null) return null;

  const lower = ageLower ?? 0;

  if (lower >= 18) return "adult";
  if (ageUpper === null) {
    // Intervalo aberto que não chega a 18: usa o piso.
    if (lower >= 16) return "16_17";
    if (lower >= 13) return "13_15";
    return "under13";
  }

  if (lower < 13) return "under13";
  if (lower < 16) return "13_15";
  return "16_17";
}

export function isAgeSignalsSupported(): boolean {
  try {
    return (
      Capacitor.isNativePlatform() &&
      Capacitor.getPlatform() === "android" &&
      Capacitor.isPluginAvailable("AgeSignals")
    );
  } catch {
    return false;
  }
}

export type AgeSignalSyncStatus =
  | "unsupported" // web, iOS ou build sem o plugin
  | "unavailable" // API/Play Store não respondeu
  | "not_shared" // elegível, mas sem faixa compartilhada
  | "adult_ignored" // faixa adulta: o servidor recusaria (art. 9º)
  | "reported" // sinal protetivo registrado no servidor
  | "report_failed";

/**
 * Consulta a loja e reporta ao servidor. Idempotente e barato de repetir: o
 * servidor sobrescreve o sinal com o mesmo valor.
 */
export async function syncPlatformAgeSignal(): Promise<AgeSignalSyncStatus> {
  if (!isAgeSignalsSupported()) return "unsupported";

  let result: AgeSignalsResult;
  try {
    result = await AgeSignals.checkAgeSignals();
  } catch (error) {
    console.warn("[AgeSignals] Consulta falhou:", error);
    return "unavailable";
  }

  if (!result?.available) return "unavailable";
  if (!result.shared) return "not_shared";

  const bracket = bracketFromAgeRange(result.ageLower ?? null, result.ageUpper ?? null);
  if (!bracket) return "not_shared";
  // Adulto vindo do cliente é autodeclaração: o servidor recusa por regra.
  if (bracket === "adult") return "adult_ignored";

  const { reportPlatformAgeSignal } = await import("./capacitor");
  const ok = await reportPlatformAgeSignal(bracket);
  return ok ? "reported" : "report_failed";
}
